import React, { useEffect, useMemo, useState } from 'react';
import { FiCreditCard, FiInfo, FiUpload } from 'react-icons/fi';
import { useLocation, useNavigate } from 'react-router-dom';
import useAPI from '../../hooks/useAPI';
import usePostPenyaluranProgram from '../../hooks/usePostPenyaluran_program';
import useUpdatePenyaluranProgram from '../../hooks/useUpdatePenyaluran_program';
import endpointsAdmin from '../../Services/endpointAdmin';
import api from '../../Services/api';
import { PENYALURAN_FUNDING_RULES, resolveProgramFundingMeta, resolveTransactionFundingMeta } from '../../Services/penyaluranFundingMap';
import {
  buildAdminMitraUserScope,
  extractArrayPayload,
  getAdminLembagaScope,
  matchItemToAdminScope,
} from './adminScope';

function findFieldDeep(obj, fieldNames) {
  if (!obj || typeof obj !== 'object') return null;
  const fields = Array.isArray(fieldNames) ? fieldNames : [fieldNames];

  for (const key of fields) {
    if (Object.prototype.hasOwnProperty.call(obj, key) && obj[key]) {
      return obj[key];
    }
  }

  for (const value of Object.values(obj)) {
    if (value && typeof value === 'object') {
      const result = findFieldDeep(value, fields);
      if (result) return result;
    }
  }

  return null;
}

function formatCurrency(value) {
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
  }).format(Number(value || 0) || 0);
}

function resolveTransactionAmount(item) {
  return Number(item?.jumlah || item?.jumlah_donasi || item?.nominal || 0) || 0;
}

function resolveTransactionStatus(item) {
  return String(item?.status || item?.status_transaksi || item?.statusTransaksi || '')
    .trim()
    .toLowerCase();
}

function isSuccessfulTransaction(item) {
  const status = resolveTransactionStatus(item);
  return ['success', 'verified', 'diverifikasi', 'berhasil', 'sukses', 'paid', 'settlement'].includes(status);
}

function resolveStoredFundingType(item) {
  return String(
    item?.sumber_dana ||
    item?.sourceType ||
    item?.source_type ||
    item?.kategori_dana ||
    item?.jenis_sumber_dana ||
    ''
  )
    .trim()
    .toLowerCase();
}

function resolvePenyaluranFundingType(item, programMetadataMap) {
  const storedType = resolveStoredFundingType(item);
  if (storedType) return storedType;

  const programMeta = programMetadataMap.get(String(item?.id_program || ''));
  return programMeta?.sourceType || '';
}

function resolveDetailDonationMeta(item, detailDonasiList) {
  const code = String(
    item?.kode_detail_donasi ||
    item?.kodeDetailDonasi ||
    item?.detail_donasi?.kode_detail_donasi ||
    item?.detail_donasi?.kode ||
    item?.detailDonasi?.kode_detail_donasi ||
    item?.detailDonasi?.kode ||
    ''
  ).trim();

  const name =
    item?.detail_donasi?.nama_detail_donasi ||
    item?.detail_donasi?.nama ||
    item?.detailDonasi?.nama_detail_donasi ||
    item?.detailDonasi?.nama ||
    item?.nama_detail_donasi ||
    '';

  const matchedDetail = detailDonasiList.find((detail) => {
    const detailCode = String(detail?.kode_detail_donasi || detail?.kode || '').trim();
    return detailCode && detailCode === code;
  });

  return {
    code: code || String(matchedDetail?.kode_detail_donasi || matchedDetail?.kode || '').trim(),
    name: name || matchedDetail?.nama_detail_donasi || matchedDetail?.nama || '',
  };
}

function DanaCategoryChart({ summary }) {
  if (!summary) {
    return (
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
        <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Saldo Dana</span>
        <p className="mt-1 text-xs text-slate-500">Pilih sumber dana untuk melihat sisa saldo yang tersedia.</p>
      </div>
    );
  }

  return (
    <div className={`rounded-xl border p-5 ${summary.cukup ? 'border-emerald-200 bg-emerald-50/70' : 'border-red-200 bg-red-50/70'}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600">Sisa Saldo</span>
          <p className="mt-1 text-xs text-slate-600">{summary.sourceLabel}</p>
        </div>
        <span className={`rounded-full px-3 py-1 text-[11px] font-bold whitespace-nowrap ${summary.cukup ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
          {summary.cukup ? '✓ Tersedia' : '✗ Terbatas'}
        </span>
      </div>

      <p className="mt-3 text-2xl font-extrabold text-slate-900">{formatCurrency(summary.danaSebelum)}</p>
      <p className="text-[11px] text-slate-500">sebelum penyaluran</p>

      {summary.nominalInput > 0 && (
        <div className="mt-3 rounded-lg bg-white/60 border border-white/80 p-3 flex items-center justify-between">
          <div>
            <p className="text-[11px] text-slate-500">Sisa setelah penyaluran</p>
            <p className="text-sm font-bold text-slate-900">{formatCurrency(summary.danaSesudah)}</p>
          </div>
          <div className="text-right">
            <p className="text-[11px] text-slate-500">Berkurang</p>
            <p className="text-sm font-bold text-red-600">- {formatCurrency(summary.nominalInput)}</p>
          </div>
        </div>
      )}
    </div>
  );
}

const initialForm = {
  id_divisi: '',
  id_program: '',
  sumber_dana: '',
  nominal_penyaluran: '',
  status_penyaluran: 'SELESAI',
  bukti_penyaluran: null,
};

const requiredFieldProps = (message) => ({
  required: true,
  title: message,
  onInvalid: (event) => event.target.setCustomValidity(message),
  onInput: (event) => event.target.setCustomValidity(''),
});

const inputClass = 'w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-orange-400 focus:bg-white focus:ring-2 focus:ring-orange-100';
const disabledInputClass = `${inputClass} disabled:bg-slate-100 disabled:cursor-not-allowed`;

function InputPenyaluranAdmin({ admin }) {
  const navigate = useNavigate();
  const location = useLocation();
  const editingItem = location.state?.item || null;
  const editingId = editingItem?.id_penyaluran || editingItem?.id_peyaluran || editingItem?.id || null;

  const { data: penyaluranData } = useAPI(endpointsAdmin.penyaluran_program.getAll);
  const { data: programData } = useAPI(endpointsAdmin.program.getAll);
  const { data: divisiData } = useAPI(endpointsAdmin.divisi.getAll);
  const { data: detailDonasiData } = useAPI(endpointsAdmin.detail_donasi.getAll);
  const { data: transaksiData } = useAPI(endpointsAdmin.transaksi.getAll);
  const { postPenyaluranProgram, loading: creating, error: createError } = usePostPenyaluranProgram();
  const { putPenyaluranProgram, loading: updating, error: updateError } = useUpdatePenyaluranProgram();
  const [adminMitraRecords, setAdminMitraRecords] = useState([]);

  const [formData, setFormData] = useState(initialForm);
  const [actionMessage, setActionMessage] = useState('');

  const lembagaScope = getAdminLembagaScope(admin);

  useEffect(() => {
    let cancelled = false;

    const fetchAdminMitra = async () => {
      if (!lembagaScope.active) {
        setAdminMitraRecords([]);
        return;
      }

      try {
        const response = await api.post(endpointsAdmin.adminmitra.createByLembaga(lembagaScope.id), {});
        if (cancelled) return;
        setAdminMitraRecords(extractArrayPayload(response.data, ['adminmitra', 'data_adminmitra', 'users']));
      } catch (fetchError) {
        if (cancelled) return;
        setAdminMitraRecords([]);
      }
    };

    fetchAdminMitra();

    return () => {
      cancelled = true;
    };
  }, [lembagaScope.active, lembagaScope.id]);

  const adminMitraScope = useMemo(
    () => buildAdminMitraUserScope(adminMitraRecords),
    [adminMitraRecords]
  );

  useEffect(() => {
    if (!editingItem) return;

    setFormData({
      id_divisi: editingItem.program?.id_divisi ? String(editingItem.program.id_divisi) : String(editingItem.id_divisi || editingItem.program?.divisi?.id_divisi || ''),
      id_program: editingItem.id_program ? String(editingItem.id_program) : '',
      sumber_dana: resolveStoredFundingType(editingItem),
      nominal_penyaluran: editingItem.nominal_penyaluran ? String(editingItem.nominal_penyaluran) : '',
      status_penyaluran: editingItem.status_penyaluran || 'SELESAI',
      bukti_penyaluran: null,
    });
  }, [editingItem]);

  const penyaluranList = useMemo(() => extractArrayPayload(penyaluranData, ['penyaluran_program']), [penyaluranData]);
  const programList = useMemo(() => extractArrayPayload(programData, ['program']), [programData]);
  const divisiList = useMemo(() => {
    const directDivisi = extractArrayPayload(divisiData, ['divisi']);
    if (directDivisi.length > 0) return directDivisi;

    const uniqueDivisi = new Map();
    programList.forEach((item) => {
      const idDivisi = String(item?.id_divisi || item?.divisi?.id_divisi || item?.divisi?.id || '').trim();
      const namaDivisi = String(item?.divisi?.nama_divisi || item?.nama_divisi || '').trim();
      if (idDivisi && namaDivisi && !uniqueDivisi.has(idDivisi)) {
        uniqueDivisi.set(idDivisi, { id_divisi: idDivisi, nama_divisi: namaDivisi });
      }
    });

    return Array.from(uniqueDivisi.values());
  }, [divisiData, programList]);
  const detailDonasiList = useMemo(() => extractArrayPayload(detailDonasiData, ['detail_donasi']), [detailDonasiData]);
  const transaksiList = useMemo(() => extractArrayPayload(transaksiData, ['transaksi']), [transaksiData]);
  const fundingSourceOptions = useMemo(
    () => PENYALURAN_FUNDING_RULES.map((rule) => ({ value: rule.type, label: rule.label })),
    []
  );

  const adminIdUser = useMemo(
    () => String(findFieldDeep(admin, ['id_user', 'idUser', 'id']) || '').trim(),
    [admin]
  );
  const adminName = useMemo(
    () => String(findFieldDeep(admin, ['nama_user', 'nama', 'name', 'username']) || 'Admin').trim(),
    [admin]
  );

  const divisiMap = useMemo(
    () => new Map(divisiList.map((item) => [String(item.id_divisi), item.nama_divisi || 'Divisi'])),
    [divisiList]
  );

  const programMetadataMap = useMemo(() => {
    const map = new Map();
    programList.forEach((program) => {
      const fundingMeta = resolveProgramFundingMeta(program);
      map.set(String(program.id_program), {
        ...program,
        divisiName: program?.divisi?.nama_divisi || divisiMap.get(String(program.id_divisi)) || 'Divisi',
        sourceType: fundingMeta.sourceType,
        sourceLabel: fundingMeta.sourceLabel,
        categoryName: fundingMeta.distributionLabel,
        hasFundingRule: fundingMeta.hasFundingRule,
      });
    });
    return map;
  }, [divisiMap, programList]);

  const filteredProgramList = useMemo(() => (
    programList.filter((item) => !formData.id_divisi || String(item.id_divisi) === String(formData.id_divisi))
  ), [formData.id_divisi, programList]);

  const donationTotalsByCategory = useMemo(() => {
    const totals = new Map();

    transaksiList.forEach((item) => {
      if (!isSuccessfulTransaction(item)) return;
      
      // Filter berdasarkan lembaga scope
      if (!matchItemToAdminScope(item, lembagaScope, adminMitraScope)) return;

      const detailMeta = resolveDetailDonationMeta(item, detailDonasiList);
      const fundingMeta = resolveTransactionFundingMeta(item, detailMeta);
      const key = fundingMeta.sourceType;
      if (!key) return;

      totals.set(key, (totals.get(key) || 0) + resolveTransactionAmount(item));
    });

    return totals;
  }, [detailDonasiList, transaksiList, lembagaScope, adminMitraScope]);

  const totalDistributedByCategory = useMemo(() => {
    const totals = new Map();

    penyaluranList.forEach((item) => {
      // Filter berdasarkan lembaga scope
      if (!matchItemToAdminScope(item, lembagaScope, adminMitraScope)) return;
      
      const key = resolvePenyaluranFundingType(item, programMetadataMap);
      if (!key) return;

      totals.set(key, (totals.get(key) || 0) + (Number(item.nominal_penyaluran) || 0));
    });

    return totals;
  }, [penyaluranList, programMetadataMap, lembagaScope, adminMitraScope]);

  const selectedProgramMeta = useMemo(
    () => programMetadataMap.get(String(formData.id_program || '')) || null,
    [formData.id_program, programMetadataMap]
  );
  const selectedFundingSource = useMemo(
    () => fundingSourceOptions.find((source) => source.value === formData.sumber_dana) || null,
    [formData.sumber_dana, fundingSourceOptions]
  );

  const selectedDanaSummary = useMemo(() => {
    if (!selectedFundingSource) return null;

    const categoryKey = selectedFundingSource.value;
    const totalDonasi = categoryKey ? (donationTotalsByCategory.get(categoryKey) || 0) : 0;
    
    // Hitung hak amil berdasarkan tipe sumber dana
    let hakAmil = 0;
    if (categoryKey === 'zakat') {
      hakAmil = Math.round(totalDonasi * 0.125); // 12.5% untuk zakat
    } else if (categoryKey === 'infak') {
      hakAmil = Math.round(totalDonasi * 0.20); // 20% untuk infak
    }
    
    const danaSetelahHakAmil = totalDonasi - hakAmil;
    
    let totalTersalur = categoryKey ? (totalDistributedByCategory.get(categoryKey) || 0) : 0;
    const editingSourceType = editingItem ? resolvePenyaluranFundingType(editingItem, programMetadataMap) : '';
    if (editingItem && editingSourceType === categoryKey) {
      totalTersalur -= Number(editingItem.nominal_penyaluran) || 0;
    }

    const nominalInput = Number(formData.nominal_penyaluran) || 0;
    const danaSebelum = danaSetelahHakAmil - totalTersalur;
    const danaSesudah = danaSebelum - nominalInput;

    return {
      categoryName: selectedProgramMeta?.categoryName || 'Penyaluran',
      sourceLabel: selectedFundingSource.label,
      hasFundingRule: true,
      totalDonasi,
      hakAmil,
      danaSetelahHakAmil,
      totalTersalur,
      nominalInput,
      danaSebelum,
      danaSesudah,
      cukup: danaSesudah >= 0,
    };
  }, [donationTotalsByCategory, editingItem, formData.nominal_penyaluran, programMetadataMap, selectedFundingSource, selectedProgramMeta, totalDistributedByCategory]);

  const currentError = createError || updateError;

  const handleChange = (event) => {
    const { name, value, files, type } = event.target;
    setFormData((prev) => ({
      ...prev,
      ...(name === 'id_divisi' ? { id_program: '' } : {}),
      [name]: type === 'file' ? files?.[0] || null : value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setActionMessage('');

    if (!formData.id_divisi || !formData.id_program || !formData.sumber_dana || !formData.nominal_penyaluran) {
      setActionMessage('Divisi, program, sumber dana, dan nominal penyaluran wajib diisi.');
      return;
    }

    if (!adminIdUser) {
      setActionMessage('ID user admin tidak ditemukan. Silakan login ulang.');
      return;
    }

    if (!selectedDanaSummary.cukup) {
      setActionMessage(`Dana ${selectedDanaSummary.sourceLabel} tidak cukup. Sisa tersedia ${formatCurrency(selectedDanaSummary.danaSebelum)}.`);
      return;
    }

    const payload = {
      id_program: formData.id_program,
      id_user: adminIdUser,
      sumber_dana: formData.sumber_dana,
      nominal_penyaluran: formData.nominal_penyaluran,
      status_penyaluran: formData.status_penyaluran,
      bukti_penyaluran: formData.bukti_penyaluran,
    };

    const result = editingId
      ? await putPenyaluranProgram(editingId, payload)
      : await postPenyaluranProgram(payload);

    if (result) {
      navigate('/penyaluran/list', {
        state: {
          message: editingId ? 'Data penyaluran berhasil diperbarui.' : 'Data penyaluran berhasil ditambahkan.',
        },
      });
    }
  };

  return (
    <div className="min-h-screen bg-slate-50/80 px-4 py-8 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl space-y-6">
        <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800 text-white shadow-[0_20px_60px_rgba(15,23,42,0.16)]">
          <div className="flex flex-col gap-6 px-6 py-8 lg:flex-row lg:items-end lg:justify-between lg:px-8">
            <div>
              <div className="mb-3 inline-flex rounded-full border border-white/20 bg-white/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.24em] text-blue-100 backdrop-blur">
                Penyaluran Program
              </div>
              <h1 className="text-3xl font-bold leading-tight lg:text-4xl">
                {editingId ? 'Ubah Data Penyaluran' : 'Input Penyaluran Program'}
              </h1>
              <p className="mt-3 max-w-2xl text-sm leading-relaxed text-slate-200">
                Catat penyaluran dana dari sumber donasi ke program yang telah terdaftar dengan alur yang lebih rapi dan mudah dipantau.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => navigate('/penyaluran/list')}
                className="rounded-2xl border border-white/20 bg-white px-4 py-2 text-xs font-semibold text-slate-800 transition hover:bg-slate-100"
              >
                ← Kembali ke List Penyaluran
              </button>
            </div>
          </div>
        </section>

        {(actionMessage || currentError) && (
          <div className={`rounded-2xl border px-4 py-3 text-sm font-semibold ${
            currentError ? 'border-red-200 bg-red-50 text-red-700' : 'border-blue-200 bg-blue-50 text-blue-700'
          }`}>
            {currentError || actionMessage}
          </div>
        )}

        <div className="grid gap-6 xl:grid-cols-[1.25fr_0.75fr]">
          <div className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.24em] text-slate-500">
                  <FiCreditCard className="text-orange-500" />
                  Petugas
                </div>
                <p className="mt-2 text-sm font-semibold text-slate-900">{adminName}</p>
                <p className="mt-1 text-[11px] text-slate-500">ID User: {adminIdUser || '-'}</p>
              </div>
              <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.24em] text-slate-500">
                  <FiInfo className="text-blue-500" />
                  Lembaga
                </div>
                <p className="mt-2 text-sm font-semibold text-slate-900">{lembagaScope.name || 'Semua Lembaga'}</p>
                <p className="mt-1 text-[11px] text-slate-500">Saldo dari lembaga {lembagaScope.active ? 'ini' : 'yang dipilih'}</p>
              </div>
            </div>

            <section className="rounded-[24px] border border-slate-200 bg-white p-6 shadow-sm">
              <div className="mb-5 border-b border-slate-100 pb-3">
                <h2 className="text-lg font-bold text-slate-900">
                  {editingId ? 'Form Ubah Penyaluran' : 'Form Input Penyaluran'}
                </h2>
                <p className="mt-1 text-sm text-slate-500">Pilih divisi, program, dan sumber dana secara berurutan untuk menjaga saldo tetap terkontrol.</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-xs font-bold uppercase tracking-[0.24em] text-slate-600">
                      Divisi <span className="text-red-500">*</span>
                    </label>
                    <select name="id_divisi" value={formData.id_divisi} onChange={handleChange} {...requiredFieldProps('Divisi wajib dipilih.')} className={inputClass}>
                      <option value="">-- Pilih Divisi --</option>
                      {divisiList.map((item) => (
                        <option key={item.id_divisi} value={item.id_divisi}>{item.nama_divisi}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="mb-1 block text-xs font-bold uppercase tracking-[0.24em] text-slate-600">
                      Program <span className="text-red-500">*</span>
                    </label>
                    <select name="id_program" value={formData.id_program} onChange={handleChange} disabled={!formData.id_divisi} {...requiredFieldProps('Program wajib dipilih.')} className={disabledInputClass}>
                      <option value="">-- Pilih Program --</option>
                      {filteredProgramList.map((item) => (
                        <option key={item.id_program} value={item.id_program}>{item.nama_program}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="mb-1 block text-xs font-bold uppercase tracking-[0.24em] text-slate-600">
                      Sumber Dana <span className="text-red-500">*</span>
                    </label>
                    <select name="sumber_dana" value={formData.sumber_dana} onChange={handleChange} {...requiredFieldProps('Sumber dana wajib dipilih.')} className={inputClass}>
                      <option value="">-- Pilih Sumber Dana --</option>
                      {fundingSourceOptions.map((source) => (
                        <option key={source.value} value={source.value}>{source.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="mb-1 block text-xs font-bold uppercase tracking-[0.24em] text-slate-600">
                      Nominal Penyaluran (Rp) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      min="0"
                      name="nominal_penyaluran"
                      value={formData.nominal_penyaluran}
                      onChange={handleChange}
                      placeholder="0"
                      {...requiredFieldProps('Nominal penyaluran wajib diisi.')}
                      className={inputClass}
                    />
                    {formData.nominal_penyaluran && (
                      <p className="mt-1 text-xs font-bold text-blue-600">
                        {formatCurrency(formData.nominal_penyaluran)}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="mb-1 block text-xs font-bold uppercase tracking-[0.24em] text-slate-600">
                      Status Penyaluran <span className="text-red-500">*</span>
                    </label>
                    <select name="status_penyaluran" value={formData.status_penyaluran} onChange={handleChange} {...requiredFieldProps('Status penyaluran wajib dipilih.')} className={inputClass}>
                      <option value="pending">Pending</option>
                      <option value="diproses">Diproses</option>
                      <option value="tersalurkan">Tersalurkan</option>
                      <option value="SELESAI">SELESAI</option>
                    </select>
                  </div>

                  <div>
                    <label className="mb-1 block text-xs font-bold uppercase tracking-[0.24em] text-slate-600">
                      Bukti Penyaluran {!editingId && <span className="text-red-500">*</span>}
                    </label>
                    <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-3">
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <FiUpload className="text-orange-500" />
                        <span>Upload bukti penyaluran</span>
                      </div>
                      <input
                        type="file"
                        name="bukti_penyaluran"
                        onChange={handleChange}
                        accept="image/*,.pdf"
                        required={!editingId}
                        title="Bukti penyaluran wajib diupload."
                        onInvalid={(event) => event.target.setCustomValidity('Bukti penyaluran wajib diupload.')}
                        onInput={(event) => event.target.setCustomValidity('')}
                        className="mt-3 block w-full text-sm text-slate-500 file:mr-3 file:rounded-full file:border-0 file:bg-orange-100 file:px-3 file:py-1.5 file:text-sm file:font-semibold file:text-orange-700 hover:file:bg-orange-200"
                      />
                    </div>
                    <p className="mt-1 text-[11px] text-slate-500">
                      {editingId ? 'Bukti lama tetap dipakai jika tidak upload file baru.' : 'Wajib upload bukti penyaluran.'}
                    </p>
                  </div>
                </div>

                {selectedProgramMeta && (
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                    <span className="text-[11px] font-bold uppercase tracking-[0.24em] text-slate-500">Program Dipilih</span>
                    <p className="mt-1 text-sm font-semibold text-slate-900">{selectedProgramMeta.categoryName}</p>
                    <p className="text-[11px] text-slate-500">Sumber dana dipilih manual dari field di atas.</p>
                  </div>
                )}

                <div className="flex justify-end pt-2">
                  <button
                    type="submit"
                    disabled={creating || updating}
                    className="inline-flex items-center justify-center rounded-2xl bg-blue-600 px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:opacity-70"
                  >
                    {creating || updating ? 'Menyimpan...' : editingId ? 'Simpan Perubahan' : 'Tambah Penyaluran'}
                  </button>
                </div>
              </form>
            </section>
          </div>

          <div className="space-y-4">
            <section className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm">
              <div className="mb-4 flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.24em] text-slate-500">
                <FiCreditCard className="text-blue-500" />
                Ringkasan Dana
              </div>
              {selectedDanaSummary ? (
                <div className="space-y-3">
                  <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-3">
                    <p className="text-sm font-semibold text-slate-900">{selectedDanaSummary.sourceLabel}</p>
                    <p className="mt-1 text-[11px] text-slate-500">{selectedDanaSummary.categoryName}</p>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                    <div className="flex items-center justify-between">
                      <span>Saldo sebelum penyaluran</span>
                      <span className="font-semibold">{formatCurrency(selectedDanaSummary.danaSebelum)}</span>
                    </div>
                    <div className="mt-2 flex items-center justify-between">
                      <span>Nominal yang diinput</span>
                      <span className="font-semibold text-red-600">- {formatCurrency(selectedDanaSummary.nominalInput)}</span>
                    </div>
                    <div className="mt-2 flex items-center justify-between border-t border-slate-200 pt-2">
                      <span>Saldo setelah penyaluran</span>
                      <span className={`font-semibold ${selectedDanaSummary.cukup ? 'text-emerald-600' : 'text-red-600'}`}>
                        {formatCurrency(selectedDanaSummary.danaSesudah)}
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-4 text-sm text-slate-500">
                  Pilih sumber dana dan nominal untuk melihat ringkasan saldo.
                </div>
              )}
            </section>

            <section className="rounded-[24px] border border-orange-200 bg-orange-50 p-5 shadow-sm">
              <div className="flex items-start gap-2">
                <FiInfo className="mt-0.5 text-orange-500" />
                <div>
                  <h3 className="text-sm font-semibold text-slate-900">Catatan penting</h3>
                  <p className="mt-1 text-sm leading-6 text-slate-600">
                    Pastikan bukti penyaluran sudah tersedia sebelum menyimpan agar pencatatan lebih mudah diverifikasi.
                  </p>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}

export default InputPenyaluranAdmin;
